import { TrendingUp, TrendingDown, AlertTriangle, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCustomerHealth, type CustomerHealthData } from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";

interface CustomerHealthPanelProps {
  dateRange: DateRange;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function CustomerList({
  customers,
  type,
}: {
  customers: CustomerHealthData["growing"] | CustomerHealthData["declining"];
  type: "growing" | "declining";
}) {
  if (!customers || customers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No {type} customers in this period
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {customers.slice(0, 10).map((customer) => (
        <div
          key={customer.customerId}
          className="flex justify-between items-center py-2 border-b last:border-0"
        >
          <div>
            <span className="font-medium">{customer.name}</span>
            {customer.customerNumber && (
              <span className="text-muted-foreground text-sm">
                {" "}
                #{customer.customerNumber}
              </span>
            )}
          </div>
          <div className="text-right text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {customer.previousPeriodOrders} → {customer.currentPeriodOrders}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  type === "growing" ? "text-green-600" : "text-red-600"
                )}
              >
                {customer.changePercent > 0 ? "+" : ""}
                {customer.changePercent.toFixed(0)}%
              </Badge>
            </div>
            <div className="text-muted-foreground">
              {formatCurrency(customer.revenueCents)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TopCustomersList({
  customers,
}: {
  customers: CustomerHealthData["topByRevenue"];
}) {
  if (!customers || customers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No revenue data in this period
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {customers.slice(0, 10).map((customer, index) => (
        <div
          key={customer.customerId}
          className="flex justify-between items-center py-2 border-b last:border-0"
        >
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                index < 3 ? "bg-primary text-primary-foreground" : "bg-muted"
              )}
            >
              {index + 1}
            </span>
            <div>
              <span className="font-medium">{customer.name}</span>
              {customer.customerNumber && (
                <span className="text-muted-foreground text-sm">
                  {" "}
                  #{customer.customerNumber}
                </span>
              )}
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-medium">
              {formatCurrency(customer.revenueCents)}
            </div>
            <div className="text-muted-foreground">
              {customer.orderCount} orders ({customer.percentOfTotal.toFixed(1)}
              %)
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConcentrationRiskBadge({
  risk,
}: {
  risk: CustomerHealthData["concentrationRisk"];
}) {
  if (!risk) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-2 p-3 rounded-md",
        risk.isHighRisk
          ? "bg-red-50 text-red-800"
          : "bg-green-50 text-green-800"
      )}
    >
      {risk.isHighRisk ? (
        <AlertTriangle className="h-4 w-4" />
      ) : (
        <Users className="h-4 w-4" />
      )}
      <div className="text-sm">
        <span className="font-medium">Concentration Risk: </span>
        <span>Top 3 customers = {risk.top3Percent.toFixed(1)}% of revenue</span>
        {risk.isHighRisk && (
          <span className="ml-2">(High - consider diversifying)</span>
        )}
      </div>
    </div>
  );
}

export default function CustomerHealthPanel({
  dateRange,
}: CustomerHealthPanelProps) {
  const { data, isLoading, error } = useCustomerHealth(dateRange);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Customer Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Customer Health</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Failed to load customer health data
          </p>
        </CardContent>
      </Card>
    );
  }

  const healthData = data;
  if (!healthData) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Customer Health</span>
          {healthData?.period && (
            <span className="text-sm font-normal text-muted-foreground">
              vs previous period
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ConcentrationRiskBadge risk={healthData?.concentrationRisk} />

        <Tabs defaultValue="top" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="top" className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              Top Revenue
            </TabsTrigger>
            <TabsTrigger value="growing" className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />
              Growing ({healthData?.growing?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="declining" className="flex items-center gap-1">
              <TrendingDown className="h-4 w-4" />
              Declining ({healthData?.declining?.length || 0})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="top">
            <TopCustomersList customers={healthData?.topByRevenue} />
          </TabsContent>
          <TabsContent value="growing">
            <CustomerList customers={healthData?.growing} type="growing" />
          </TabsContent>
          <TabsContent value="declining">
            <CustomerList customers={healthData?.declining} type="declining" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
